import styled, { css } from 'styled-components';
import UIFlex from 'UIComponents/layout/UIFlex';
import { EERIE } from 'HubStyleTokens/colors';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILink from 'UIComponents/link/UILink';
import useLocale from '../hooks/useLocale';
import { useViewport } from '../hooks/useViewport';
import { mediaQueries, MOBILE_MENU_BREAKPOINT } from '../utils/mediaQueries';
import { LinkWithEnglishSuperset } from './Link';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Overrides descendant text properties in this section.
 */
const sharedTextCSS = css(["font-size:16px;line-height:16px;"]);
const H4 = styled.h4.withConfig({
  displayName: "RelatedDocs__H4",
  componentId: "xamovs-0"
})(["", " margin:0 0 0 6px;", "{margin-left:0;}", "{margin-right:4px;}"], sharedTextCSS, mediaQueries.belowWidth(MOBILE_MENU_BREAKPOINT), mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT));
const StyledUIFlex = styled(UIFlex).withConfig({
  displayName: "RelatedDocs__StyledUIFlex",
  componentId: "xamovs-1"
})(["margin:32px 0 25px 0;", " ", ";"], sharedTextCSS, ({
  relatedDocType
}) => relatedDocType === 'guide' ? ` ${mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT)} {
      margin-top: 53px;
    }` : '');

/**
 * External non-router-aware `UILink` when not on developers.hubspot.com, otherwise
 * strip the domain to make `to` absolute.
 *
 * TODO: Add tests and other envs if we go this way
 */
// the passed in hrefs come from SHADE and are either external or referencing the qa or prod domain
const URL_REGEX = /(https:\/\/)?developers\.(wt)?hubspot\.(com|de|es|fr|jp)(\/beta-docs)?/g;
const getRelativeHref = href => href.replace(URL_REGEX, '');
const Link = ({
  external,
  href,
  children,
  showEnTag,
  getEnglishBaseUrl
}) => {
  if (external) {
    return /*#__PURE__*/_jsx(UILink, {
      external: true,
      href: href,
      children: children
    });
  }
  const relativeHref = getRelativeHref(href);
  if (showEnTag) {
    return /*#__PURE__*/_jsx(LinkWithEnglishSuperset, {
      href: getEnglishBaseUrl(relativeHref),
      children: children
    });
  }
  return /*#__PURE__*/_jsx(UIRouterLink, {
    to: relativeHref,
    children: children
  });
};
const StyledUIIcon = styled(UIIcon).withConfig({
  displayName: "RelatedDocs__StyledUIIcon",
  componentId: "xamovs-2"
})(["", "{margin-top:2px;}"], mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT));

/**
 * The 'Related guide' section shown in an API's intro and use case card.
 */
const RelatedDocs = ({
  href,
  title,
  relatedDocType,
  localization
}) => {
  const viewportContext = useViewport();
  const locale = useLocale();
  const external = href.match(URL_REGEX) === null && href.charAt(0) !== '/';
  const relativeHref = getRelativeHref(href);
  const localizationProps = localization && !external && !localization.isUrlLocalized(relativeHref, locale) ? {
    showEnTag: true,
    getEnglishBaseUrl: localization.getEnglishBaseUrl
  } : {
    showEnTag: false
  };
  const docTypeMap = {
    guide: {
      icon: 'bulb',
      link: /*#__PURE__*/_jsxs(UIFlex, {
        direction: viewportContext.isMobile ? 'column' : 'row',
        gap: 8,
        align: "baseline",
        children: [/*#__PURE__*/_jsx(H4, {
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "developer-docs-shared-components.relatedDocs.relatedGuideTitle"
          })
        }), /*#__PURE__*/_jsx(Link, Object.assign({
          external: external,
          href: href
        }, localizationProps, {
          children: title
        }))]
      })
    },
    api: {
      icon: 'code',
      link: /*#__PURE__*/_jsx(Link, Object.assign({
        external: external,
        href: href
      }, localizationProps, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "developer-docs-shared-components.relatedDocs.relatedApiTitle",
          options: {
            apiLabel: title
          }
        })
      }))
    }
  };
  return /*#__PURE__*/_jsxs(StyledUIFlex, {
    direction: "row",
    gap: 8,
    relatedDocType: relatedDocType,
    align: "center",
    children: [/*#__PURE__*/_jsx(StyledUIIcon, {
      name: docTypeMap[relatedDocType].icon,
      size: 16,
      color: EERIE
    }), docTypeMap[relatedDocType].link]
  });
};
export default RelatedDocs;